import { useState } from 'react';
import { Icon } from '../Icon';
import { surveyQuestions, surveyCategoryMeta } from '../../constants/survey';
import type { CategoryCounts } from '../../lib/peerReportSummary';
import type { EvaluatorPool, Member, PeerSubTab, SessionUser } from '../../types';

type CurrentPool = EvaluatorPool | { selectedIds: string[]; confirmed: boolean } | null;

interface PeerEvalTabProps {
    sessionUser: SessionUser;
    myEvaluationTargets: Member[];
    myGivenAnswers: Record<string, Record<number, string>>;
    mySubmittedColleagueIds: Set<string>;
    activeColleagueToEvaluate: Member | null;
    setActiveColleagueToEvaluate: (m: Member | null) => void;
    handleAnswerSelect: (colleague: Member, questionId: number, value: string) => void;
    commentStrength: string;
    setCommentStrength: (v: string) => void;
    commentImprovement: string;
    setCommentImprovement: (v: string) => void;
    handleSaveDraft: (colleague: Member) => void;
    handleCompleteEvaluation: (colleague: Member) => void;
    receivedParticipants: number;
    receivedByCategory: CategoryCounts;
    receivedStrengths: string[];
    receivedImprovements: string[];
    selectedPoolSubject: string;
    setSelectedPoolSubject: (v: string) => void;
    departmentMembers: Member[];
    evaluatorPools: Record<string, EvaluatorPool>;
    currentPool: CurrentPool;
    currentPoolCount: number;
    poolRecommendedIds: string[];
    poolRecommendedSet: Set<string>;
    handleApplyRecommendedPool: () => void;
    handleTogglePoolMember: (memberId: string) => void;
    handleConfirmEvaluatorPool: () => void;
    handleUnlockEvaluatorPool: () => void;
}

export function PeerEvalTab({
    sessionUser,
    myEvaluationTargets,
    myGivenAnswers,
    mySubmittedColleagueIds,
    activeColleagueToEvaluate,
    setActiveColleagueToEvaluate,
    handleAnswerSelect,
    commentStrength,
    setCommentStrength,
    commentImprovement,
    setCommentImprovement,
    handleSaveDraft,
    handleCompleteEvaluation,
    receivedParticipants,
    receivedByCategory,
    receivedStrengths,
    receivedImprovements,
    selectedPoolSubject,
    setSelectedPoolSubject,
    departmentMembers,
    evaluatorPools,
    currentPool,
    currentPoolCount,
    poolRecommendedIds,
    poolRecommendedSet,
    handleApplyRecommendedPool,
    handleTogglePoolMember,
    handleConfirmEvaluatorPool,
    handleUnlockEvaluatorPool,
}: PeerEvalTabProps) {
    const [peerSubTab, setPeerSubTab] = useState<PeerSubTab>(sessionUser.role === 'EVALUATOR' ? 'selection' : 'survey');
    const categoryKeys = Object.keys(surveyCategoryMeta) as (keyof typeof surveyCategoryMeta)[];

    return (
        <div>
            <div className="flex gap-2 bg-slate-100 p-1.5 rounded-xl border border-slate-200 mb-6 max-w-md">
                <button
                    onClick={() => setPeerSubTab('survey')}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${peerSubTab === 'survey' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                    <Icon name="edit-3" className="w-3.5 h-3.5" /> 다면평가 참여하기
                </button>
                <button
                    onClick={() => setPeerSubTab('report')}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${peerSubTab === 'report' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                    <Icon name="bar-chart-2" className="w-3.5 h-3.5" /> 피드백 결과 리포트
                </button>
                {(sessionUser.role === 'EVALUATOR' || sessionUser.role === 'HR') && (
                    <button
                        onClick={() => setPeerSubTab('selection')}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${peerSubTab === 'selection' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                    >
                        <Icon name="settings" className="w-3.5 h-3.5" /> 평가자 매칭/관리
                    </button>
                )}
            </div>

            {peerSubTab === 'survey' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm h-fit">
                        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <Icon name="check-square" className="w-4 h-4 text-indigo-600" /> 평가 대상 동료 목록
                        </h3>
                        <p className="text-xs text-slate-500 mb-4">리더가 확정한 매칭 결과에 따라, 함께 협업한 동료의 일하는 방식을 피드백합니다.</p>
                        {myEvaluationTargets.length === 0 && (
                            <div className="text-xs text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded-xl p-4 text-center leading-relaxed">
                                아직 배정된 평가 대상이 없습니다.<br />리더가 평가자 풀을 확정하면 이곳에 대상이 표시됩니다.
                            </div>
                        )}
                        <div className="space-y-2">
                            {myEvaluationTargets.map(target => {
                                const answeredCount = Object.keys(myGivenAnswers[target.id] || {}).length;
                                const isSubmitted = mySubmittedColleagueIds.has(target.id);
                                const isFilled = answeredCount === surveyQuestions.length;
                                return (
                                    <div
                                        key={target.id}
                                        onClick={() => setActiveColleagueToEvaluate(target)}
                                        className={`p-3.5 rounded-xl border transition-all cursor-pointer flex justify-between items-center ${
                                            activeColleagueToEvaluate && activeColleagueToEvaluate.id === target.id
                                                ? 'bg-indigo-50/50 border-indigo-300 ring-1 ring-indigo-300'
                                                : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                                        }`}
                                    >
                                        <div>
                                            <h4 className="font-bold text-slate-900 text-sm">{target.name} {target.duty}</h4>
                                            <p className="text-[10px] text-slate-400 mt-0.5">답변 완료: {answeredCount} / {surveyQuestions.length}</p>
                                        </div>
                                        {isSubmitted ? (
                                            <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2.5 py-1 rounded-full border border-emerald-200 flex items-center gap-1">
                                                <Icon name="check" className="w-3 h-3" /> 제출완료
                                            </span>
                                        ) : isFilled ? (
                                            <span className="bg-sky-50 text-sky-700 text-[10px] font-bold px-2.5 py-1 rounded-full border border-sky-200">
                                                제출 전
                                            </span>
                                        ) : (
                                            <span className="bg-amber-50 text-amber-700 text-[10px] font-bold px-2.5 py-1 rounded-full border border-amber-200">
                                                진행중
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        {activeColleagueToEvaluate ? (
                            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                                {(() => {
                                    const surveyLocked = mySubmittedColleagueIds.has(activeColleagueToEvaluate.id);
                                    return (
                                    <>
                                    <div className="flex justify-between items-center pb-4 border-b border-slate-100 mb-6">
                                        <div>
                                            <h3 className="text-base font-bold text-slate-900">
                                                <span className="text-indigo-600 font-extrabold">{activeColleagueToEvaluate.name}</span> 동료 다면평가 설문
                                            </h3>
                                            <p className="text-xs text-slate-500 mt-1">
                                                {surveyLocked
                                                    ? '제출이 완료되어 수정할 수 없습니다. 응답은 익명으로 집계됩니다.'
                                                    : '질문에 대해 가장 직관적인 응답을 선택해 주세요 (Yes/No 형식). 응답 내용은 익명으로 집계됩니다.'}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setActiveColleagueToEvaluate(null)}
                                            className="text-slate-400 hover:text-slate-600 text-xs flex items-center gap-1.5"
                                        >
                                            <Icon name="arrow-left" className="w-3.5 h-3.5" /> 목록으로
                                        </button>
                                    </div>

                                    <div className={`space-y-6 ${surveyLocked ? 'opacity-75 pointer-events-none' : ''}`}>
                                        {surveyQuestions.map((q, idx) => {
                                            const currentAnswer = (myGivenAnswers[activeColleagueToEvaluate.id] || {})[q.id];
                                            return (
                                                <div key={q.id} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                                                    <div className="flex justify-between items-start gap-4 mb-3">
                                                        <div>
                                                            <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full mb-1 inline-block">
                                                                {q.category === 'Ownership' ? 'Ownership' : q.category === 'Co-work' ? 'Teamwork & Co-work' : 'Professionalism'}
                                                            </span>
                                                            <h4 className="text-sm font-semibold text-slate-800 leading-relaxed">
                                                                {idx + 1}. {q.text}
                                                            </h4>
                                                        </div>
                                                    </div>

                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAnswerSelect(activeColleagueToEvaluate, q.id, 'GOOD')}
                                                            className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all flex items-center justify-center gap-1.5 ${
                                                                currentAnswer === 'GOOD'
                                                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                                                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                                                            }`}
                                                        >
                                                            <Icon name="smile" className="w-3.5 h-3.5" /> Good (우수함)
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAnswerSelect(activeColleagueToEvaluate, q.id, 'BAD')}
                                                            className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all flex items-center justify-center gap-1.5 ${
                                                                currentAnswer === 'BAD'
                                                                    ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                                                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                                                            }`}
                                                        >
                                                            <Icon name="frown" className="w-3.5 h-3.5" /> Bad (보완필요)
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAnswerSelect(activeColleagueToEvaluate, q.id, 'NA')}
                                                            className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all flex items-center justify-center gap-1.5 ${
                                                                currentAnswer === 'NA'
                                                                    ? 'bg-slate-600 text-white border-slate-600 shadow-sm'
                                                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                                                            }`}
                                                        >
                                                            <Icon name="help-circle" className="w-3.5 h-3.5" /> N/A (판단불가)
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* 서술형 코멘트 (실제 리포트에 익명으로 반영) */}
                                        <div className="p-4 rounded-xl bg-emerald-50/40 border border-emerald-100">
                                            <label className="block text-xs font-bold text-emerald-800 mb-1.5 flex items-center gap-1">
                                                <Icon name="thumbs-up" className="w-3.5 h-3.5" /> 이 동료의 업무상 강점 (선택)
                                            </label>
                                            <textarea
                                                value={commentStrength}
                                                onChange={(e) => setCommentStrength(e.target.value)}
                                                placeholder="예: 신규 과제에도 늘 먼저 손을 들고 끝까지 완수합니다."
                                                className="w-full bg-white border border-emerald-200 rounded-xl px-3 py-2 text-xs h-16 focus:outline-none focus:ring-2 focus:ring-emerald-400 leading-relaxed"
                                            ></textarea>
                                        </div>
                                        <div className="p-4 rounded-xl bg-rose-50/40 border border-rose-100">
                                            <label className="block text-xs font-bold text-rose-800 mb-1.5 flex items-center gap-1">
                                                <Icon name="help-circle" className="w-3.5 h-3.5" /> 개선/보완이 필요한 점 (선택)
                                            </label>
                                            <textarea
                                                value={commentImprovement}
                                                onChange={(e) => setCommentImprovement(e.target.value)}
                                                placeholder="예: 기한 압박 시 커뮤니케이션 빈도가 낮아지는 경향이 있습니다."
                                                className="w-full bg-white border border-rose-200 rounded-xl px-3 py-2 text-xs h-16 focus:outline-none focus:ring-2 focus:ring-rose-400 leading-relaxed"
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="mt-8 pt-4 border-t border-slate-100 flex justify-end gap-3">
                                        {surveyLocked ? (
                                            <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-lg">
                                                제출 완료 · 수정 불가
                                            </span>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => handleSaveDraft(activeColleagueToEvaluate)}
                                                    className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-4 py-2 rounded-lg font-bold text-xs"
                                                >
                                                    나중에 이어서 작성
                                                </button>
                                                <button
                                                    onClick={() => handleCompleteEvaluation(activeColleagueToEvaluate)}
                                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg font-bold text-xs shadow-md"
                                                >
                                                    다면평가 완료 제출
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    </>
                                        );
                                    })()}
                            </div>
                        ) : (
                            <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm flex flex-col items-center justify-center min-h-[300px]">
                                <div className="bg-indigo-50 p-4 rounded-full text-indigo-600 mb-4">
                                    <Icon name="user-check" className="w-8 h-8" />
                                </div>
                                <h3 className="text-base font-bold text-slate-900 mb-2">설문 대상 동료를 선택해 주세요</h3>
                                <p className="text-sm text-slate-500 max-w-sm mb-4">왼쪽에 표시된 협업 동료를 클릭하여 다면평가를 바로 진행할 수 있습니다.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {peerSubTab === 'report' && (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-slate-100 mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900">나의 일하는 방식 다면평가 피드백 리포트</h3>
                            <p className="text-xs text-slate-500 mt-1">협업 동료들이 관찰·평가한 결과입니다. 누가 평가했는지는 공개되지 않습니다.</p>
                        </div>
                        <div className="bg-indigo-50 border border-indigo-100 px-4 py-2.5 rounded-xl flex items-center gap-2">
                            <Icon name="info" className="w-4 h-4 text-indigo-400" />
                            <span className="text-xs font-semibold text-indigo-900">
                                {receivedParticipants > 0 ? `${receivedParticipants}명의 협업 동료가 참여한 결과입니다.` : '아직 수신된 다면평가 응답이 없습니다.'}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {categoryKeys.map(catKey => {
                            const meta = surveyCategoryMeta[catKey];
                            const data = receivedByCategory[catKey];
                            const answeredTotal = data.good + data.bad;
                            const positivityRate = answeredTotal > 0 ? Math.round((data.good / answeredTotal) * 100) : 0;
                            return (
                                <div key={catKey} className="bg-slate-50 rounded-2xl p-5 border border-slate-200 flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-start gap-2 mb-3">
                                            <h4 className="font-bold text-sm text-slate-950 leading-tight">{meta.title}</h4>
                                            <span className="px-2.5 py-1 rounded-full text-xs font-extrabold bg-indigo-50 text-indigo-700 border border-indigo-200">
                                                {answeredTotal > 0 ? `${positivityRate}%` : '-'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 leading-relaxed mb-6">{meta.desc}</p>
                                    </div>
                                    <div>
                                        <div className="w-full bg-slate-200 h-2.5 rounded-full mb-3 overflow-hidden">
                                            <div className="h-full rounded-full bg-indigo-600 transition-all" style={{ width: `${positivityRate}%` }}></div>
                                        </div>
                                        <div className="flex justify-between items-center text-[11px] text-slate-500">
                                            <span>Good {data.good}건</span>
                                            <span>Bad {data.bad}건</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-8 pt-6 border-t border-slate-100">
                        <h4 className="font-bold text-sm text-slate-900 mb-4 flex items-center gap-2">
                            <Icon name="message-square" className="w-4 h-4 text-indigo-600" /> 동료 서술 피드백 및 코멘트 (익명)
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4">
                                <h5 className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1">
                                    <Icon name="thumbs-up" className="w-3.5 h-3.5" /> 업무상의 강점 (Strengths)
                                </h5>
                                {receivedStrengths.length > 0 ? (
                                    <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4 leading-relaxed">
                                        {receivedStrengths.map((s, i) => <li key={i}>{s}</li>)}
                                    </ul>
                                ) : (
                                    <p className="text-xs text-slate-400">아직 수신된 강점 코멘트가 없습니다.</p>
                                )}
                            </div>

                            <div className="bg-rose-50/50 border border-rose-100 rounded-xl p-4">
                                <h5 className="text-xs font-bold text-rose-800 mb-2 flex items-center gap-1">
                                    <Icon name="help-circle" className="w-3.5 h-3.5" /> 개선 및 보완 필요 사항 (Needs Improvement)
                                </h5>
                                {receivedImprovements.length > 0 ? (
                                    <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4 leading-relaxed">
                                        {receivedImprovements.map((s, i) => <li key={i}>{s}</li>)}
                                    </ul>
                                ) : (
                                    <p className="text-xs text-slate-400">아직 수신된 보완 코멘트가 없습니다.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {peerSubTab === 'selection' && (sessionUser.role === 'EVALUATOR' || sessionUser.role === 'HR') && (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-slate-100 mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900">팀원 다면평가자 Pool 매칭 및 확정</h3>
                            <p className="text-xs text-slate-500 mt-1">피평가 대상자를 선택한 뒤, 해당 인원을 평가할 동료 풀을 구성하고 확정합니다. 확정 즉시 지정된 동료에게 설문이 노출됩니다.</p>
                        </div>
                        {selectedPoolSubject && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-500">현재 선택된 다면평가자: </span>
                                <span className={`px-3 py-1 rounded-full text-xs font-extrabold ${currentPoolCount >= 3 && currentPoolCount <= 8 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                                    {currentPoolCount}명 (권장 6~8명)
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="mb-6">
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">피평가 대상자 선택</label>
                        <select
                            value={selectedPoolSubject}
                            onChange={(e) => setSelectedPoolSubject(e.target.value)}
                            className="w-full md:w-96 bg-slate-50 border border-slate-300 text-sm font-semibold rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                        >
                            <option value="">-- 대상자를 선택하세요 --</option>
                            {departmentMembers.map(m => (
                                <option key={m.id} value={m.id}>
                                    {m.name} {m.duty} ({m.department}) {evaluatorPools[m.id]?.confirmed ? '· 풀 확정됨' : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedPoolSubject ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <h4 className="font-bold text-sm text-slate-900 mb-3 flex items-center gap-2">
                                    <Icon name="list-filter" className="text-indigo-600" /> 협업 동료 Pooling 데이터
                                </h4>
                                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                                    * 중점과제에 지정된 Co-worker와 동일 부서 인원이 추천 표시됩니다. 체크 후 확정하세요.
                                </p>
                                {!currentPool?.confirmed && (
                                    <button
                                        type="button"
                                        onClick={handleApplyRecommendedPool}
                                        className="mb-4 w-full bg-violet-50 hover:bg-violet-100 text-violet-800 border border-violet-200 text-xs font-bold px-3 py-2 rounded-xl transition-all"
                                    >
                                        Co-worker·동일부서 추천 {poolRecommendedIds.length}명 일괄 반영
                                    </button>
                                )}

                                <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2">
                                    {departmentMembers.filter(m => m.id !== selectedPoolSubject).map(m => {
                                        const isChecked = (currentPool?.selectedIds || []).includes(m.id);
                                        const isRecommended = poolRecommendedSet.has(m.id);
                                        return (
                                            <label key={m.id} className={`flex items-center justify-between p-3.5 rounded-xl border cursor-pointer transition-all ${isChecked ? 'bg-indigo-50/40 border-indigo-200' : isRecommended ? 'bg-violet-50/50 border-violet-200' : 'bg-slate-50 border-slate-200 hover:bg-slate-100/50'}`}>
                                                <div className="flex items-center gap-3">
                                                    <input type="checkbox" checked={isChecked} disabled={currentPool?.confirmed} onChange={() => handleTogglePoolMember(m.id)} className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4" />
                                                    <div>
                                                        <span className="font-bold text-sm text-slate-900">{m.name}</span>
                                                        <span className="text-xs text-slate-400 block mt-0.5">{m.department}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    {isRecommended && (
                                                        <span className="text-[10px] font-bold text-violet-700 bg-violet-100 border border-violet-200 px-1.5 py-0.5 rounded">추천</span>
                                                    )}
                                                    <span className="text-xs text-slate-500 bg-white border border-slate-200 px-2 py-1 rounded">{m.duty}</span>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 h-fit">
                                <h4 className="font-bold text-sm text-slate-900 mb-3 flex items-center gap-2">
                                    <Icon name="shield" className="text-indigo-600" /> 다면평가 운영 가이드
                                </h4>
                                <ul className="text-xs text-slate-600 space-y-3 pl-4 list-disc leading-relaxed">
                                    <li><strong>평가 인원 준수:</strong> 통계 신뢰도 보장을 위해 6~8명 구성을 권장하며, 소규모 조직은 최소 3명부터 확정 가능합니다.</li>
                                    <li><strong>매칭 추천 기준:</strong> 중점 업무 등록 시 지정된 Co-worker 및 동일 조직 구성원을 우선 검토하세요.</li>
                                    <li><strong>기밀성 보장:</strong> 개별 동료가 부여한 피드백은 익명으로 집계되며, 피평가자 리포트에는 응답자 정보가 노출되지 않습니다.</li>
                                </ul>

                                <div className="mt-8 pt-4 border-t border-slate-200 flex justify-end">
                                    {currentPool?.confirmed ? (
                                        <div className="w-full space-y-2">
                                            <div className="bg-indigo-100 text-indigo-700 text-xs font-bold px-4 py-2.5 rounded-xl flex items-center gap-1.5 w-full justify-center border border-indigo-200">
                                                <Icon name="lock" className="w-3.5 h-3.5" /> 다면 평가자 풀이 최종 확정되었습니다
                                            </div>
                                            <button onClick={handleUnlockEvaluatorPool} className="w-full bg-white hover:bg-slate-100 text-slate-500 border border-slate-300 px-4 py-2 rounded-xl font-bold text-xs transition-all">
                                                확정 해제 후 재조정
                                            </button>
                                        </div>
                                    ) : (
                                        <button onClick={handleConfirmEvaluatorPool} className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-2">
                                            <Icon name="check-circle" className="w-4 h-4" /> 다면 평가자 풀 최종 확정
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-400 text-sm">
                            위 드롭다운에서 피평가 대상자를 먼저 선택해주세요.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
